import React, { useMemo } from 'react';
import { Search, Star } from 'lucide-react';

export default function HotelFilterSidebar({ filters, setFilters, availableStars, availablePensions, hotels }) {
  
  // Count hotels per star rating
  const starCounts = useMemo(() => {
    const counts = {};
    availableStars.forEach(star => {
      counts[star] = hotels.filter(h => h.stars === star).length;
    });
    return counts;
  }, [availableStars, hotels]);

  // Count hotels per pension type and get labels
  const pensionInfo = useMemo(() => {
    const info = {};
    
    hotels.forEach(hotel => {
      hotel.pensions?.forEach(pension => {
        if (!info[pension.type]) {
          info[pension.type] = {
            label: pension.label || pension.code || pension.type,
            count: 0
          };
        }
        info[pension.type].count++;
      });
    });
    
    return info;
  }, [hotels]);

  const handleStarChange = (stars) => {
    const newStars = filters.stars.includes(stars)
      ? filters.stars.filter(s => s !== stars)
      : [...filters.stars, stars];
    setFilters({ ...filters, stars: newStars });
  };

  const handlePensionChange = (pension) => {
    const newPensions = filters.pensions.includes(pension)
      ? filters.pensions.filter(p => p !== pension)
      : [...filters.pensions, pension];
    setFilters({ ...filters, pensions: newPensions });
  };

  return (
    <div className="bg-white rounded-xl shadow-md p-6 sticky top-24">
      <h2 className="text-xl font-bold text-[#2B4979] mb-6">Filtrer Par:</h2>

      {/* Search by name */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Nom d'hôtel:
        </label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            value={filters.name}
            onChange={(e) => setFilters({ ...filters, name: e.target.value })}
            placeholder="Rechercher un hôtel..."
            className="w-full pl-10 pr-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#FF7B47] focus:outline-none transition"
          />
        </div>
      </div>

      <hr className="my-6 border-gray-200" />

      {/* Stars Category - Dynamic */}
      {availableStars.length > 0 && (
        <>
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Catégorie:
            </label>
            <div className="space-y-2">
              {availableStars.map((star) => (
                <label key={star} className="flex items-center justify-between cursor-pointer group">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={filters.stars.includes(star)}
                      onChange={() => handleStarChange(star)}
                      className="w-4 h-4 text-[#FF7B47] border-gray-300 rounded focus:ring-[#FF7B47] cursor-pointer"
                    />
                    <span className="flex items-center gap-2 text-gray-700 group-hover:text-[#FF7B47] transition">
                      <span className="text-yellow-500 flex">
                        {[...Array(star)].map((_, i) => (
                          <Star key={i} size={14} className="fill-yellow-400" />
                        ))}
                      </span>
                    </span>
                  </div>
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                    {starCounts[star] || 0}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <hr className="my-6 border-gray-200" />
        </>
      )}

      {/* Pension Formula - Dynamic */}
      {availablePensions.length > 0 && (
        <>
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Formule d'hébergement:
            </label>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {availablePensions.map((pension) => {
                const info = pensionInfo[pension] || { label: pension, count: 0 };
                
                return (
                  <label key={pension} className="flex items-center justify-between cursor-pointer group">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <input
                        type="checkbox"
                        checked={filters.pensions.includes(pension)}
                        onChange={() => handlePensionChange(pension)}
                        className="w-4 h-4 text-[#FF7B47] border-gray-300 rounded focus:ring-[#FF7B47] cursor-pointer flex-shrink-0"
                      />
                      <span className="text-sm text-gray-700 group-hover:text-[#FF7B47] transition truncate">
                        {info.label}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full ml-2 flex-shrink-0">
                      {info.count}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <hr className="my-6 border-gray-200" />
        </>
      )}

      {/* Reset Filters */}
      <button
        onClick={() => setFilters({ name: '', stars: [], pensions: [] })}
        className="w-full py-2 text-sm text-[#FF7B47] hover:bg-[#FF7B47] hover:text-white border-2 border-[#FF7B47] rounded-lg transition font-semibold"
      >
        Réinitialiser les filtres
      </button>

      {/* Active Filters Count */}
      {(filters.name || filters.stars.length > 0 || filters.pensions.length > 0) && (
        <div className="mt-4 text-center text-sm text-gray-600">
          <span className="font-semibold text-[#FF7B47]">
            {filters.stars.length + filters.pensions.length + (filters.name ? 1 : 0)}
          </span> filtre(s) actif(s)
        </div>
      )}
    </div>
  );
}